/**
 * Product Price with Fees Manager
 * Tracks selected custom options with fees and updates the product price display accordingly
 */

class ProductPriceWithFeesManager {
  constructor() {
    this.feeProductPrices = new Map(); // Cache for fee product prices
    this.quantityDebounceTimer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachListeners());
    } else {
      this.attachListeners();
    }

    // Re-attach listeners after section reload
    document.addEventListener('shopify:section:load', () => {
      this.attachListeners();
    });
  }

  attachListeners() {
    // Find all product forms
    const productForms = document.querySelectorAll('product-form-component');
    
    productForms.forEach(form => {
      const formElement = form.querySelector('form');
      if (!formElement) return;

      // Skip if already initialized
      if (form.dataset.feePriceInitialized) return;
      form.dataset.feePriceInitialized = 'true';

      // Listen for changes on all inputs within fee-enabled blocks
      const feeBlocks = formElement.querySelectorAll('[data-fee-config]');
      
      feeBlocks.forEach(block => {
        // Add listeners to all inputs within this block
        const inputs = block.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
          if (!input.dataset.feePriceListenerAttached) {
            input.addEventListener('change', () => this.handleFeeChange(formElement));
            input.addEventListener('input', () => this.handleFeeChange(formElement));
            input.dataset.feePriceListenerAttached = 'true';
          }
        });
      });

      // Listen for quantity changes at the form level for better reliability
      formElement.addEventListener('quantity-selector:update', () => {
        // Clear any pending debounce
        if (this.quantityDebounceTimer) {
          clearTimeout(this.quantityDebounceTimer);
        }
        // Immediate update on quantity selector component events
        this.handleFeeChange(formElement);
      });

      // Also listen on the input directly as a fallback
      const quantityInput = formElement.querySelector('input[name="quantity"]');
      if (quantityInput && !quantityInput.dataset.feePriceQuantityListenerAttached) {
        quantityInput.addEventListener('change', () => {
          // Clear any pending debounce
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          // Immediate update on change event
          this.handleFeeChange(formElement);
        });
        quantityInput.addEventListener('input', () => {
          // Debounce input events to avoid excessive recalculation
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          this.quantityDebounceTimer = setTimeout(() => {
            this.handleFeeChange(formElement);
          }, 300);
        });
        
        // Use MutationObserver to catch value changes that don't trigger events
        // (e.g., when stepUp/stepDown are called programmatically)
        const observer = new MutationObserver(() => {
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          this.quantityDebounceTimer = setTimeout(() => {
            this.handleFeeChange(formElement);
          }, 100);
        });
        
        observer.observe(quantityInput, {
          attributes: true,
          attributeFilter: ['value']
        });
        
        // Also observe the value property changes
        let lastValue = quantityInput.value;
        const checkValue = () => {
          if (quantityInput.value !== lastValue) {
            lastValue = quantityInput.value;
            if (this.quantityDebounceTimer) {
              clearTimeout(this.quantityDebounceTimer);
            }
            this.quantityDebounceTimer = setTimeout(() => {
              this.handleFeeChange(formElement);
            }, 100);
          }
        };
        
        // Poll for value changes as a last resort fallback
        setInterval(checkValue, 500);
        
        quantityInput.dataset.feePriceQuantityListenerAttached = 'true';
      }

      // Initial calculation - use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        this.handleFeeChange(formElement);
      }, 100);
    });
  }

  /**
   * Handle fee selection change
   * @param {HTMLFormElement} form 
   */
  async handleFeeChange(form) {
    const productId = form.closest('product-form-component')?.dataset.productId;
    if (!productId) return;

    // Collect all selected fees
    const fees = await this.collectSelectedFees(form);
    
    // Calculate total fee amount
    let totalFeeAmount = 0;
    for (const fee of fees) {
      totalFeeAmount += fee.price;
    }

    // Update the price display
    this.updatePriceDisplay(productId, totalFeeAmount);
  }

  /**
   * Collect all currently selected fees from the form
   * @param {HTMLFormElement} form 
   * @returns {Promise<Array<{handle: string, price: number}>>}
   */
  async collectSelectedFees(form) {
    const fees = [];
    const formData = new FormData(form);
    const blocks = form.querySelectorAll('[data-fee-config]');
    
    // Get product quantity from the form - try multiple methods
    let productQuantity = parseInt(formData.get('quantity'), 10);
    
    // Fallback: check for quantity input directly
    if (!productQuantity || isNaN(productQuantity)) {
      const quantityInput = form.querySelector('input[name="quantity"]');
      productQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    }
    
    // Final fallback
    if (!productQuantity || isNaN(productQuantity)) {
      productQuantity = 1;
    }

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const feeConfig = this.parseFeeConfig(block);
      const blockType = block.dataset.feeType;
      const propertyKey = block.dataset.key || block.dataset.feePropertyKey;

      if (!feeConfig || !feeConfig.enabled) {
        continue;
      }

      switch (blockType) {
        case 'checkbox': {
          const checkbox = block.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.checked && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity });
              }
            }
          }
          break;
        }

        case 'text': {
          const value = formData.get(`properties[${propertyKey}]`);
          if (value && value.toString().trim() !== '' && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity });
              }
            }
          }
          break;
        }

        case 'select':
        case 'radio': {
          const value = formData.get(`properties[${propertyKey}]`);
          if (value && value.toString().trim() !== '' && feeConfig.optionFees) {
            const optionKey = value.toString().trim().toLowerCase();
            const optionFeeConfig = feeConfig.optionFees[optionKey];
            if (optionFeeConfig) {
              const feeData = this.parseFeeData(optionFeeConfig, productQuantity);
              if (feeData) {
                const price = await this.getFeeProductPrice(feeData.handle);
                if (price !== null) {
                  fees.push({ handle: feeData.handle, price: price * feeData.quantity });
                }
              }
            }
          }
          break;
        }

        case 'multiselect':
        case 'swatch-checkbox': {
          const value = formData.get(`properties[${propertyKey}]`);
          if (value && feeConfig.optionFees) {
            const selectedOptions = value.toString().split(',').map(v => v.trim().toLowerCase());
            for (const option of selectedOptions) {
              const optionFeeConfig = feeConfig.optionFees[option];
              if (optionFeeConfig) {
                const feeData = this.parseFeeData(optionFeeConfig, productQuantity);
                if (feeData) {
                  const price = await this.getFeeProductPrice(feeData.handle);
                  if (price !== null) {
                    fees.push({ handle: feeData.handle, price: price * feeData.quantity });
                  }
                }
              }
            }
          }
          break;
        }

        case 'file': {
          const fileInput = block.querySelector('input[type="file"]');
          if (fileInput?.files?.length > 0 && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity });
              }
            }
          }
          break;
        }

        case 'personalization': {
          const textKey = block.dataset.personalizationTextKey;
          const textValue = textKey ? formData.get(`properties[${textKey}]`) : null;
          const fileInput = block.querySelector('input[type="file"]');
          const hasText = textValue && textValue.toString().trim() !== '';
          const hasFile = fileInput?.files?.length > 0;

          if ((hasText || hasFile) && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity });
              }
            }
          }
          break;
        }
      }
    }

    return fees;
  }

  /**
   * Parse fee data and determine quantity
   * @param {Object|string} feeConfig - Fee configuration (can be string or object)
   * @param {number} productQuantity - Product quantity
   * @returns {Object|null} - {handle: string, quantity: number} or null if waived
   */
  parseFeeData(feeConfig, productQuantity) {
    // Handle both old format (string) and new format (object)
    let handle, type, minQtyWaive;
    
    if (typeof feeConfig === 'string') {
      // Old format: just the product handle
      handle = feeConfig;
      type = 'one-time';
      minQtyWaive = null;
    } else {
      // New format: object with handle, type, and optional minQtyWaive
      handle = feeConfig.handle || feeConfig.productHandle;
      type = (feeConfig.type || 'one-time').toLowerCase().trim();
      minQtyWaive = feeConfig.minQtyWaive || null;
    }
    
    if (!handle) return null;

    // Check if fee should be waived based on quantity threshold
    if (minQtyWaive) {
      const threshold = parseInt(minQtyWaive, 10);
      if (!isNaN(threshold) && productQuantity >= threshold) {
        return null; // Skip this fee
      }
    }

    // Calculate fee quantity based on type (case-insensitive)
    let feeQuantity = 1;
    if (type === 'per-item') {
      feeQuantity = productQuantity;
    }

    return { handle, quantity: feeQuantity };
  }

  /**
   * Parse fee configuration from block element
   * @param {HTMLElement} block 
   * @returns {Object|null}
   */
  parseFeeConfig(block) {
    try {
      const config = JSON.parse(block.dataset.feeConfig || '{}');
      return config;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get fee product price (with caching)
   * @param {string} handle 
   * @returns {Promise<number|null>}
   */
  async getFeeProductPrice(handle) {
    // Check cache first
    if (this.feeProductPrices.has(handle)) {
      return this.feeProductPrices.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        return null;
      }

      const product = await response.json();
      if (!product.variants || product.variants.length === 0) {
        return null;
      }

      // Get price in cents
      const price = product.variants[0].price;
      
      // Cache it
      this.feeProductPrices.set(handle, price);
      
      return price;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update the price display with fees
   * @param {string} productId 
   * @param {number} totalFeeAmount - Total fee amount in cents
   */
  updatePriceDisplay(productId, totalFeeAmount) {
    // Find the product-price element for this product
    const priceElement = document.querySelector(`product-price[data-product-id="${productId}"]`);
    if (!priceElement) return;

    const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    // Find the main price element
    const priceSpan = priceContainer.querySelector('.price');
    if (!priceSpan) return;

    // Store original price if not already stored
    if (!priceSpan.dataset.originalPrice) {
      priceSpan.dataset.originalPrice = priceSpan.textContent.trim();
    }

    // If no fees, restore original price
    if (totalFeeAmount === 0) {
      priceSpan.textContent = priceSpan.dataset.originalPrice;
      // Remove any fee indicator
      const feeIndicator = priceContainer.querySelector('.price-fee-indicator');
      if (feeIndicator) {
        feeIndicator.remove();
      }
      return;
    }

    // Get the original price value in cents (we need to parse it)
    const originalText = priceSpan.dataset.originalPrice;
    const originalPriceCents = this.parsePriceToCents(originalText);
    
    if (originalPriceCents === null) return;

    // Calculate new total
    const newTotalCents = originalPriceCents + totalFeeAmount;
    
    // Format the new price
    const newPriceFormatted = this.formatPrice(newTotalCents);
    
    // Update the price
    priceSpan.textContent = newPriceFormatted;

    // Add a fee indicator if it doesn't exist
    let feeIndicator = priceContainer.querySelector('.price-fee-indicator');
    if (!feeIndicator) {
      feeIndicator = document.createElement('span');
      feeIndicator.className = 'price-fee-indicator';
      feeIndicator.style.cssText = 'display: block; font-size: 0.85em; color: rgb(var(--color-foreground-rgb) / 0.7); margin-top: 0.25rem;';
      priceContainer.appendChild(feeIndicator);
    }
    
    const feeFormatted = this.formatPrice(totalFeeAmount);
    feeIndicator.textContent = `(Includes ${feeFormatted} in fees)`;
  }

  /**
   * Parse a price string to cents
   * @param {string} priceText 
   * @returns {number|null}
   */
  parsePriceToCents(priceText) {
    // Remove currency symbols and whitespace
    const cleaned = priceText.replace(/[^0-9.,]/g, '');
    
    // Handle different decimal separators
    // Check if comma is used as decimal separator (European format)
    if (cleaned.includes(',') && !cleaned.includes('.')) {
      // Format: 1.234,56 or 1234,56
      const withoutThousandsSep = cleaned.replace(/\./g, '');
      const normalized = withoutThousandsSep.replace(',', '.');
      return Math.round(parseFloat(normalized) * 100);
    } else {
      // Format: 1,234.56 or 1234.56 (US format)
      const normalized = cleaned.replace(/,/g, '');
      return Math.round(parseFloat(normalized) * 100);
    }
  }

  /**
   * Format cents to money string
   * @param {number} cents 
   * @returns {string}
   */
  formatPrice(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize the manager
if (!window.productPriceWithFeesManager) {
  window.productPriceWithFeesManager = new ProductPriceWithFeesManager();
}
